'use client'

import { TRoomDetail, TSimpleReviewCount } from '@/entities/room'
import { cn, useMultiScrollState } from '@/shared/lib'
import { Separator } from '@/shared/ui'

import { Heading } from '../common'
import { ReviewDialogTrigger } from './review-dialog'
import {
  MAX_REVIEW_DISPLAY_COUNT,
  ReviewList,
  ShowAllReviewsButton,
} from './review-list'
import { ReviewScore } from './review-score'
import { SimpleReview } from './simple-review'
import { TabValue } from '../../lib/tab'

type Props = Pick<TRoomDetail, 'reviewScore' | 'reviewList'> & {
  roomSimpleReview: TSimpleReviewCount[]
}

export const Review = ({
  reviewScore,
  reviewList,
  roomSimpleReview,
}: Props) => {
  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  const totalReviewCount = reviewList.length

  const hasSimpleReview = roomSimpleReview.length > 0
  const hasReviewList = totalReviewCount > 0

  const hasNoReview = !hasSimpleReview && !hasReviewList

  const isMoreThanMaxReviewDisplayCount =
    reviewList.length > MAX_REVIEW_DISPLAY_COUNT

  return (
    <section
      className="@container"
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.REVIEW, element)
        }
      }}
    >
      <Heading className={cn('mb-4', hasNoReview && 'mb-6')}>후기</Heading>
      {(() => {
        if (hasNoReview) {
          return (
            <p className="typo-body2 font-normal text-gray-900">
              첫 후기를 기다리고 있어요
            </p>
          )
        }

        if (hasSimpleReview && !hasReviewList) {
          return <SimpleReview simpleReviews={roomSimpleReview} />
        }

        if (!hasSimpleReview && hasReviewList) {
          return (
            <>
              <ReviewScore reviewScore={reviewScore} className="mb-7.5" />
              <ReviewList reviewList={reviewList} />
              {isMoreThanMaxReviewDisplayCount && (
                <ReviewDialogTrigger
                  reviewList={reviewList}
                  reviewScore={reviewScore}
                  roomSimpleReview={roomSimpleReview}
                >
                  <ShowAllReviewsButton totalReviewCount={totalReviewCount} />
                </ReviewDialogTrigger>
              )}
            </>
          )
        }

        return (
          <>
            <ReviewScore reviewScore={reviewScore} className="mb-6" />
            <SimpleReview simpleReviews={roomSimpleReview} />
            <Separator color="gray" className="my-7.5" />
            <ReviewList reviewList={reviewList} />
            {isMoreThanMaxReviewDisplayCount && (
              <ReviewDialogTrigger
                reviewList={reviewList}
                reviewScore={reviewScore}
                roomSimpleReview={roomSimpleReview}
              >
                <ShowAllReviewsButton totalReviewCount={totalReviewCount} />
              </ReviewDialogTrigger>
            )}
          </>
        )
      })()}
    </section>
  )
}
